import { isNative } from "@/utils/platform";
import { config } from "../config";
import { getBaseHeaders, handleApiError } from "./utils";
import { LoginResponse } from "@/types/auth";
import { nativeTokenStore } from "./nativeTokenStore";
import { setUserId } from "./validation";
import { tokenManager } from "./tokenManager";
import { z } from "zod";

const NATIVE_SCHEME = "com.buyfors.app";

// Web/server OAuth client — the `aud` claim of the id_token the backend verifies.
const GOOGLE_WEB_CLIENT_ID = "912817756539-7svhb9abgt7gmoo7p12b3as53810rcsf.apps.googleusercontent.com";
// iOS OAuth client (Bundle ID com.buyfors.app) — used by the native iOS sign-in.
const GOOGLE_IOS_CLIENT_ID = "912817756539-ul17tfgc97rmqvgd9m4q8h07f6nd6ter.apps.googleusercontent.com";

// SocialLogin.initialize() only needs to run once per app session.
let googleAuthInitialized = false;

const buildSsoLoginUrl = async (path: string, inviteCode?: string): Promise<string> => {
  const url = new URL(`${config.API_URL}${path}`);
  if (inviteCode) {
    url.searchParams.append('invite_code', inviteCode);
  }
  if (isNative()) {
    url.searchParams.append('platform', 'native');
    url.searchParams.append('redirect_scheme', NATIVE_SCHEME);
  }

  const fetchInit: RequestInit = {
    method: 'GET',
    headers: getBaseHeaders(),
  };
  if (!isNative()) {
    fetchInit.credentials = 'include';
  }

  const response = await fetch(url.toString(), fetchInit);
  if (!response.ok) {
    await handleApiError(response, 'Get SSO login URL');
  }

  const data = await response.json();
  if (!data.redirect_url) {
    throw new Error('SSO redirect URL not found in response');
  }
  return data.redirect_url as string;
};

export const getGoogleSSOLoginUrl = (inviteCode?: string) =>
  buildSsoLoginUrl('/user/sso/google/login', inviteCode);

export const getAppleSSOLoginUrl = (inviteCode?: string) =>
  buildSsoLoginUrl('/user/sso/apple/login', inviteCode);

const ExchangeResponseSchema = z.object({
  access_token: z.string().min(10),
  refresh_token: z.string().min(10),
  access_token_expires_in: z.number().int().positive(),
  refresh_token_expires_in: z.number().int().positive(),
  user_id: z.number().int().positive(),
});

export const exchangeNativeSSOCode = async (code: string): Promise<LoginResponse> => {
  if (!code || typeof code !== 'string') {
    throw new Error('SSO exchange code is required');
  }
  if (!/^[A-Za-z0-9_\-]{16,256}$/.test(code)) {
    throw new Error('Invalid SSO exchange code');
  }

  const response = await fetch(`${config.API_URL}/user/sso/native/exchange`, {
    method: 'POST',
    headers: getBaseHeaders(),
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    await handleApiError(response, 'SSO exchange');
  }

  const raw = await response.json();
  const parsed = ExchangeResponseSchema.parse(raw);

  const result: LoginResponse = {
    access_token: parsed.access_token,
    refresh_token: parsed.refresh_token,
    access_token_expires_in: parsed.access_token_expires_in,
    refresh_token_expires_in: parsed.refresh_token_expires_in,
    user_id: parsed.user_id,
  };

  await nativeTokenStore.save({
    access_token: result.access_token,
    refresh_token: result.refresh_token,
    user_id: result.user_id,
  });

  setUserId(result.user_id);
  tokenManager.resetRefreshFailed();

  return result;
};

export const signInWithGoogleNative = async (inviteCode?: string): Promise<LoginResponse> => {
  if (!isNative()) {
    throw new Error('Google native sign-in is only available on native platforms');
  }

  const { SocialLogin } = await import('@capgo/capacitor-social-login');

  if (!googleAuthInitialized) {
    await SocialLogin.initialize({
      google: {
        // Android + the id_token audience the backend verifies.
        webClientId: GOOGLE_WEB_CLIENT_ID,
        // iOS: app client + server client (server client mirrors webClientId).
        iOSClientId: GOOGLE_IOS_CLIENT_ID,
        iOSServerClientId: GOOGLE_WEB_CLIENT_ID,
      },
    });
    googleAuthInitialized = true;
  }

  const loginResult = await SocialLogin.login({
    provider: 'google',
    options: {},
  });

  const googleResult = loginResult.result;
  const idToken =
    googleResult && 'idToken' in googleResult ? googleResult.idToken : null;
  if (!idToken) {
    throw new Error('Google sign-in did not return an id token');
  }

  const response = await fetch(`${config.API_URL}/user/sso/google/native`, {
    method: 'POST',
    headers: getBaseHeaders(),
    body: JSON.stringify({
      id_token: idToken,
      invite_code: inviteCode,
    }),
  });

  if (!response.ok) {
    await handleApiError(response, 'Google native sign-in');
  }

  const raw = await response.json();
  const parsed = ExchangeResponseSchema.parse(raw);

  const result: LoginResponse = {
    access_token: parsed.access_token,
    refresh_token: parsed.refresh_token,
    access_token_expires_in: parsed.access_token_expires_in,
    refresh_token_expires_in: parsed.refresh_token_expires_in,
    user_id: parsed.user_id,
  };

  await nativeTokenStore.save({
    access_token: result.access_token,
    refresh_token: result.refresh_token,
    user_id: result.user_id,
  });

  setUserId(result.user_id);
  tokenManager.resetRefreshFailed();

  return result;
};


export const signInWithAppleNative = async (inviteCode?: string): Promise<LoginResponse> => {
  if (!isNative()) {
    throw new Error('Apple native sign-in is only available on native platforms');
  }

  const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');

  const appleRes = await SignInWithApple.authorize({
    clientId: 'com.buyfors.app',
    redirectURI: 'https://api.buyfors.com/user/sso/apple/callback',
    scopes: 'email name',
    state: 'native',
  });

  const identityToken = appleRes?.response?.identityToken;
  if (!identityToken) {
    throw new Error('Apple sign-in did not return an identity token');
  }

  const givenName = appleRes?.response?.givenName ?? '';
  const familyName = appleRes?.response?.familyName ?? '';
  const fullName = `${givenName} ${familyName}`.trim() || undefined;

  const response = await fetch(`${config.API_URL}/user/sso/apple/native`, {
    method: 'POST',
    headers: getBaseHeaders(),
    body: JSON.stringify({
      identity_token: identityToken,
      full_name: fullName,
      invite_code: inviteCode,
    }),
  });

  if (!response.ok) {
    await handleApiError(response, 'Apple native sign-in');
  }

  const raw = await response.json();
  const parsed = ExchangeResponseSchema.parse(raw);

  const result: LoginResponse = {
    access_token: parsed.access_token,
    refresh_token: parsed.refresh_token,
    access_token_expires_in: parsed.access_token_expires_in,
    refresh_token_expires_in: parsed.refresh_token_expires_in,
    user_id: parsed.user_id,
  };

  await nativeTokenStore.save({
    access_token: result.access_token,
    refresh_token: result.refresh_token,
    user_id: result.user_id,
  });
  setUserId(result.user_id);
  tokenManager.resetRefreshFailed();

  return result;
};
